import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Calendar, MapPin, Users, ArrowRight, CheckCircle2, Clock, Lock, Share2,
  Link as LinkIcon, X, Plus, Trash2,
} from "lucide-react";
import TopNav from "../components/TopNav";
import Orbs from "../components/Orbs";
import { Section, Overline } from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { captureUtmFromLocation, getUtmForRegistration } from "../lib/utm";
import { fmtTimePT, fmtDatePT, fmtWeekdayPT, fmtRangePT, TZ } from "../lib/time";
import RichText from "../components/RichText";
import { resolveImageUrl } from "../lib/media";
import VerifyGate from "../components/VerifyGate";
import RegistrationForm from "../components/RegistrationForm";
import { needsVerification } from "../lib/register";

const emailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export default function EventDetail() {
  const { id } = useParams();
  const { user, member } = useAuth();
  const [ev, setEv] = useState(null);
  const [notFound, setNotFound] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [verified, setVerified] = useState(null);

  useEffect(() => {
    captureUtmFromLocation();
    api.get(`/events/${id}`)
      .then((r) => setEv(r.data))
      .catch((err) => setNotFound(err.response?.status === 403 ? "members" : "missing"));
  }, [id]);

  const share = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied");
  };

  if (notFound === "members") return (
    <div className="relative min-h-screen">
      <Orbs />
      <div className="relative z-10">
        <TopNav /><Section className="pt-16" innerClassName="max-w-lg mx-auto text-center">
          <div className="glass-panel p-10" data-testid="event-members-gate">
            <Lock size={22} className="mx-auto" style={{ color: "hsl(var(--vd-purple-ink))" }} />
            <h1 className="mt-4 font-heading text-2xl">Members-only event</h1>
            <p className="mt-2 text-sm text-muted-foreground">Log in with your Venture Dock member account to see this event.</p>
            <div className="mt-6 flex justify-center gap-2">
              <Link to={`/login?next=${encodeURIComponent(`/events/${id}`)}`} className="btn-primary">Log in</Link>
            </div>
          </div>
        </Section>
      </div>
    </div>
  );

  if (notFound === "missing") return (
    <div className="relative min-h-screen">
      <Orbs /><div className="relative z-10">
        <TopNav /><Section className="pt-16" innerClassName="max-w-md mx-auto text-center">
          <div className="glass-panel p-10" data-testid="event-not-found">
            <h1 className="font-heading text-2xl">Event not found</h1>
            <p className="mt-2 text-muted-foreground">The link may have expired or the event was removed.</p>
            <Link to="/events" className="btn-primary mt-6 inline-flex">All events</Link>
          </div>
        </Section>
      </div>
    </div>
  );

  if (!ev) return (
    <div className="relative min-h-screen"><Orbs /><div className="relative z-10"><TopNav /><Section className="pt-12"><div className="skeleton h-96" /></Section></div></div>
  );

  // Phase 4e: a slotted event (1:1 session) books through the slot page —
  // redirect deep links (grid cards already point there).
  if (ev.format === "slots") return <Navigate to={`/events/capital/${ev.id}`} replace />;

  const { dateLine, timeRange } = fmtRangePT(ev.start_at, ev.end_at);
  const spotsLeft = ev.capacity != null ? Math.max(0, ev.capacity - (ev.registration_count || 0)) : null;
  const soldOut = spotsLeft === 0 && !ev.waitlist_enabled;

  const ctaLabel = ev.require_approval ? "Request to join"
    : (spotsLeft === 0 && ev.waitlist_enabled) ? "Join waiting list"
    : "Register";

  return (
    <div className="relative min-h-screen" data-testid="event-detail-page">
      <Orbs />
      <div className="relative z-10">
        <TopNav variant={user?.role === "admin" ? "admin" : "public"} />
        <Section className="pt-8 pb-24" innerClassName="grid lg:grid-cols-[1.4fr_1fr] gap-8">
          {/* Left column */}
          <div>
            <div className="glass-panel overflow-hidden">
              {/* Square for Luma covers, wide for native — see AdminEvents. */}
              <div className={`overflow-hidden relative ${ev.source === "luma" ? "aspect-square" : "aspect-[16/9]"}`}>
                {ev.cover_image ? (
                  <img src={resolveImageUrl(ev.cover_image)} alt={ev.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="aurora-cover w-full h-full" />
                )}
              </div>
              <div className="p-8">
                <div className="flex flex-wrap gap-2">
                  <span className={`chip ${ev.visibility === "public" ? "chip-peach" : ev.visibility === "members_only" ? "chip-purple" : "chip-magenta"}`}>
                    {ev.visibility === "public" ? "Public" : ev.visibility === "members_only" ? <><Lock size={10} />Members only</> : "Internal"}
                  </span>
                  {ev.require_approval && <span className="chip chip-cream">Approval required</span>}
                  {ev.ticket_price > 0 && <span className="chip chip-green">${ev.ticket_price}</span>}
                </div>
                <h1 className="mt-4 text-4xl md:text-5xl font-heading font-medium tracking-tight">{ev.title}</h1>

                <div className="mt-6 grid md:grid-cols-2 gap-4">
                  <InfoTile icon={Calendar} accent="peach" title="When">
                    <div className="mt-0.5 font-medium">{dateLine}</div>
                    <div className="text-sm text-muted-foreground font-mono">{timeRange} · PT</div>
                  </InfoTile>
                  <InfoTile icon={MapPin} accent="green" title="Where">
                    <div className="mt-0.5 font-medium">{ev.campus?.name || ev.custom_venue || (ev.virtual_link ? "Virtual" : "TBD")}</div>
                    {ev.campus?.address && <div className="text-sm text-muted-foreground">{ev.campus.address}</div>}
                  </InfoTile>
                </div>

                {ev.hosts?.length > 0 && (
                  <div className="mt-6">
                    <div className="overline">Hosted by</div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {ev.hosts.map((h, i) => (
                        <div key={h.member_id || h.email || `host-${i}`} className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full glass-panel">
                          <div className="h-7 w-7 rounded-full flex items-center justify-center text-xs font-medium"
                               style={{ background: h.role === "creator" ? "hsl(var(--vd-green))" : "hsl(var(--vd-purple))", color: h.role === "creator" ? "#fff" : "hsl(var(--vd-purple-ink))" }}>
                            {h.name?.[0]}
                          </div>
                          <span className="text-sm">{h.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-8">
                  <div className="overline">About</div>
                  <RichText html={ev.description} className="mt-3 text-foreground/90 leading-relaxed" testId="event-description" />
                </div>
              </div>
            </div>

            {ev.campus?.address && (
              <div className="glass-panel p-6 mt-6" data-testid="event-map">
                <Overline>Location</Overline>
                <div className="mt-3 font-heading">{ev.campus.name}</div>
                <div className="text-sm text-muted-foreground mt-1">{ev.campus.address}</div>
                <div className="mt-4 rounded-2xl overflow-hidden border border-white/60">
                  <iframe title="map" src={`https://www.google.com/maps?q=${encodeURIComponent(ev.campus.address)}&output=embed`} className="w-full h-64" />
                </div>
              </div>
            )}
          </div>

          {/* Right column — registration widget */}
          <div>
            <div className="glass-panel p-6 sticky top-24" data-testid="registration-panel">
              {confirmation ? (
                <Confirmation ev={ev} state={confirmation} onReset={() => { setConfirmation(null); setShowForm(false); setVerified(null); }} />
              ) : showForm ? (
                (needsVerification(ev) && !verified) ? (
                  <VerifyGate targetId={ev.id} onCancel={() => setShowForm(false)} onVerified={setVerified} />
                ) : (
                  <RegistrationForm
                    ev={ev}
                    ctaLabel={ctaLabel}
                    verified={verified}
                    onCancel={() => { setShowForm(false); setVerified(null); }}
                    onDone={(state) => setConfirmation(state)}
                  />
                )
              ) : (
                <>
                  <Overline>Registration</Overline>
                  <div className="mt-2 font-heading text-xl">
                    {soldOut ? "Sold out" : ctaLabel}
                  </div>
                  {ev.require_approval && (
                    <div className="mt-3 card-cream px-3 py-2 text-xs flex items-center gap-1.5" style={{ color: "hsl(var(--vd-peach-ink))" }} data-testid="approval-notice">
                      <Clock size={12} /> Approval required · usually within 24h
                    </div>
                  )}
                  {ev.capacity != null && (
                    <div className="mt-3 text-sm font-mono text-muted-foreground">
                      <span style={{ color: spotsLeft === 0 ? "hsl(var(--vd-peach-ink))" : "hsl(var(--vd-green))" }}>
                        {spotsLeft === 0 && ev.waitlist_enabled ? "Waitlist open" : `${spotsLeft} spots left`}
                      </span>
                      <span className="text-muted-foreground"> · capacity {ev.capacity}</span>
                    </div>
                  )}

                  {(ev.registration_count || 0) > 0 && (
                    <div className="mt-5 flex items-center gap-2">
                      <div className="flex -space-x-2">
                        {[...Array(Math.min(4, ev.registration_count))].map((_, i) => (
                          <div key={i} className="h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-medium text-white ring-2 ring-white"
                               style={{ background: ["hsl(var(--vd-green))", "hsl(var(--vd-purple))", "hsl(var(--vd-peach))", "hsl(var(--vd-teal))"][i] }}>
                            {String.fromCharCode(65 + i)}
                          </div>
                        ))}
                      </div>
                      <span className="text-xs font-mono text-muted-foreground">
                        {ev.registration_count} going
                      </span>
                    </div>
                  )}

                  <button
                    onClick={() => setShowForm(true)}
                    disabled={soldOut || !ev.registration_open}
                    className={`w-full mt-6 ${ev.require_approval ? "btn-peach" : "btn-primary"}`}
                    data-testid="event-register-btn"
                  >
                    {soldOut ? "Sold out" : ctaLabel} <ArrowRight size={14} />
                  </button>

                  {member && <div className="text-xs mt-3 text-center" style={{ color: "hsl(var(--vd-green))" }}>✓ Signed in as {user.first_name}</div>}

                  {/* Share controls live inside the panel, below the Register button.
                      They used to be a separate panel underneath the sticky column,
                      which the (often taller-than-viewport) sticky registration panel
                      overlapped while scrolling. */}
                  <div className="mt-5 pt-4 border-t border-white/10 flex items-center gap-2">
                    <button onClick={share} className="btn-ghost flex-1 text-sm" data-testid="share-copy-btn"><LinkIcon size={13} /> Copy link</button>
                    <a
                      href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(ev.title)}`}
                      target="_blank" rel="noreferrer"
                      className="btn-ghost text-sm"
                    ><Share2 size={13} /> Share</a>
                  </div>
                </>
              )}
            </div>
          </div>
        </Section>

        {showForm && null /* inline form now renders inside the panel above */}
      </div>
    </div>
  );
}

function InfoTile({ icon: Icon, accent, title, children }) {
  const bg = { peach: "hsl(var(--vd-peach) / 0.14)", green: "hsl(var(--vd-green) / 0.14)", purple: "hsl(var(--vd-purple) / 0.24)" }[accent];
  const fg = { peach: "hsl(var(--vd-peach-ink))", green: "hsl(var(--vd-green))", purple: "hsl(var(--vd-purple-ink))" }[accent];
  return (
    <div className="flex items-start gap-3 p-4 rounded-2xl glass-panel">
      <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: bg, color: fg }}>
        <Icon size={18} />
      </div>
      <div>
        <div className="text-xs text-muted-foreground uppercase tracking-widest">{title}</div>
        {children}
      </div>
    </div>
  );
}


function Confirmation({ ev, state, onReset }) {
  const buildIcs = () => {
    const fmt = (d) => new Date(d).toISOString().replace(/[-:.]/g, "").slice(0, 15) + "Z";
    return [
      "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Venture Dock//EN",
      "BEGIN:VEVENT",
      `UID:${crypto.randomUUID()}@venturedock`,
      `DTSTAMP:${fmt(new Date().toISOString())}`,
      `DTSTART:${fmt(ev.start_at)}`,
      `DTEND:${fmt(ev.end_at)}`,
      `SUMMARY:${ev.title}`,
      `LOCATION:${ev.campus?.address || ev.custom_venue || ""}`,
      "END:VEVENT", "END:VCALENDAR",
    ].join("\r\n");
  };
  const download = () => {
    const b = new Blob([buildIcs()], { type: "text/calendar" });
    const url = URL.createObjectURL(b);
    const a = document.createElement("a");
    a.href = url; a.download = `${ev.title.slice(0, 20)}.ics`; a.click();
    URL.revokeObjectURL(url);
  };
  const gcal = () => {
    const fmt = (d) => new Date(d).toISOString().replace(/[-:.]/g, "").slice(0, 15) + "Z";
    const url = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(ev.title)}&dates=${fmt(ev.start_at)}/${fmt(ev.end_at)}&details=${encodeURIComponent(ev.description || "")}&location=${encodeURIComponent(ev.campus?.address || ev.custom_venue || "")}`;
    window.open(url, "_blank");
  };

  const isPending = state.status === "pending";
  const isWait = state.status === "waitlist";

  return (
    <div className="text-center py-4" data-testid="registration-confirmation">
      <div className="h-14 w-14 rounded-full mx-auto flex items-center justify-center" style={{ background: isPending ? "hsl(var(--vd-peach) / 0.18)" : "hsl(var(--vd-green) / 0.14)", color: isPending ? "hsl(var(--vd-peach-ink))" : "hsl(var(--vd-green))" }}>
        {isPending ? <Clock size={26} /> : <CheckCircle2 size={26} />}
      </div>
      <div className="mt-4 font-heading text-2xl">
        {isPending ? "Request pending" : isWait ? "On the waitlist" : "You're going"}
      </div>
      <div className="text-sm text-muted-foreground mt-1">
        {isPending ? "Admin confirms within 24 hours." : isWait ? "We'll notify you if a spot frees up." : "See you at the door."}
      </div>

      <div className="mt-6 glass-panel p-4 text-left" data-testid="confirmation-details">
        <div className="overline">Event</div>
        <div className="font-medium text-sm mt-0.5">{ev.title}</div>
        <div className="text-xs text-muted-foreground font-mono mt-1">
          {fmtWeekdayPT(ev.start_at)} · {fmtDatePT(ev.start_at, { month: "short", day: "numeric" })} · {fmtTimePT(ev.start_at)}
        </div>
        {ev.campus?.name && <div className="text-xs text-muted-foreground mt-0.5">{ev.campus.name}</div>}
      </div>

      {!isPending && state.qr && (
        <div className="mt-4 glass-panel p-5 text-center" data-testid="qr-pass">
          <div className="overline">Check-in pass</div>
          <div className="mt-3 aspect-square max-w-[160px] mx-auto rounded-xl bg-white border border-white/70 flex items-center justify-center">
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(state.qr)}&color=3D736A`}
              alt="QR code"
              width={160} height={160}
              className="p-2"
            />
          </div>
          <div className="mt-3 font-mono text-[10px] text-muted-foreground truncate">{state.qr}</div>
        </div>
      )}

      {!isPending && (
        <div className="mt-4 flex flex-col gap-2">
          <button onClick={download} className="btn-ghost text-sm" data-testid="add-cal-ics">Add to Apple Calendar</button>
          <button onClick={gcal} className="btn-ghost text-sm" data-testid="add-cal-google">Add to Google Calendar</button>
        </div>
      )}
    </div>
  );
}
